import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import moment from 'moment';
import {
  ArrowDownTrayIcon,
  DocumentTextIcon,
  ClipboardIcon,
} from '@heroicons/react/24/outline';
import MessageCard from '../../components/utils/MessageCard';
import GraphqlService from '../../service/graphqlService';
import DateRangePicker from '../../components/utils/DateRangePicker';

export default function NewUsersList() {
  const [selectedStartDate, setSelectedStartDate] = useState(null);
  const [selectedEndDate, setSelectedEndDate] = useState(null);
  const [selectedFormattedStartDate, setSelectedFormattedStartDate] = useState(null);
  const [selectedFormattedEndDate, setSelectedFormattedEndDate] = useState(null);
  const [selectedStartTime, setSelectedStartTime] = useState('00:00');
  const [selectedEndTime, setSelectedEndTime] = useState('23:59');
  const [isSearchTriggered, setIsSearchTriggered] = useState(false);

  const {
    data: usersData,
    error: errorFetching,
    isLoading,
    refetch,
  } = useQuery(['newUsers', selectedFormattedStartDate, selectedFormattedEndDate],
    async () => {
      if (!selectedStartDate || !selectedEndDate) return [];
      return GraphqlService.getNewUsersBetweenDates(
        selectedStartDate,
        selectedEndDate,
      );
    }, {
    enabled: isSearchTriggered && selectedFormattedStartDate !== null && selectedFormattedEndDate !== null,
    cacheTime: 300000,
    staleTime: 300000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval: false,
  });

  const handleDateChangeCallback = (start, end) => {
    setSelectedStartDate(start);
    setSelectedEndDate(end);

    setSelectedFormattedStartDate(start ? moment(start).startOf('day').format('YYYY-MM-DDTHH:mm:ssZ') : null);
    setSelectedFormattedEndDate(end ? moment(end).endOf('day').format('YYYY-MM-DDTHH:mm:ssZ') : null);
    setIsSearchTriggered(false);
  };

  const downloadCsv = () => {
    if (!usersData || !usersData.length) return;

    let csvContent = 'User ID,First Action Date,Email,Cognito Group\n';
    usersData.forEach((user) => {
      const row = `${user.username},${user.createdDate},${user.email},"${user.groups}"`;
      csvContent += `${row}\n`;
    });

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'NewUsersList.csv');
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
  };

  const downloadXls = () => {
    if (!usersData || !usersData.length) return;

    const worksheet = XLSX.utils.json_to_sheet(usersData.map(user => ({
      'User ID': user.username,
      'First Action Date': user.createdDate,
      'Email': user.email,
      'Cognito Group': user.groups.join(', ')
    })));
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Users');

    const xlsBuffer = XLSX.write(workbook, { bookType: 'xls', type: 'array' });
    const blob = new Blob([xlsBuffer], { type: 'application/vnd.ms-excel' });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'NewUsersList.xls');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyUsersInClipboard = () => {
    if (!usersData) return;
    const emails = usersData.map((user) => user.email).join(';');
    navigator.clipboard.writeText(emails);
    alert('User emails copied to clipboard!');
  };

  function renderContent() {
    if (errorFetching) {
      return <MessageCard message={`Error: ${errorFetching.message}`} />;
    }

    if (!isSearchTriggered && (selectedFormattedStartDate === null || selectedFormattedEndDate === null)) {
      return <MessageCard message="Select a date range to start the search" />;
    }

    if (!isSearchTriggered) {
      return <MessageCard message="Press Search to find users for the selected date range" />;
    }

    if (isLoading) {
      return (
        <div className="w-full text-center p-4">
          <p className="text-lg text-gray-600">Retrieving new users, please wait...</p>
          <div className="mt-4 animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      );
    }

    if (usersData?.length > 0) {
      return (
        <div className="flex flex-col space-y-4 mt-4">
          <div className="flex flex-wrap justify-end items-center gap-2 pb-4">
            <button
              id="btnCSV"
              type="button"
              onClick={downloadCsv}
              className="flex items-center bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-150 ease-in-out"
            >
              <ArrowDownTrayIcon className="h-5 w-5 mr-2" />
              CSV
            </button>
            <button
              id="btnXLS"
              type="button"
              onClick={downloadXls}
              className="flex items-center bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-150 ease-in-out"
            >
              <DocumentTextIcon className="h-5 w-5 mr-2" />
              XLS
            </button>
            <button
              id="btnCopyEmails"
              type="button"
              onClick={copyUsersInClipboard}
              className="flex items-center bg-purple-500 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg shadow-md transition duration-150 ease-in-out"
            >
              <ClipboardIcon className="h-5 w-5 mr-2" />
              CLIPBOARD EMAILS
            </button>
          </div>

          <div className="overflow-x-auto shadow-lg rounded-lg scrollbar scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-100">
            <table className="min-w-full divide-y divide-gray-200 bg-white">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User ID</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">First Action Date</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cognito Group</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {usersData.map((user) => (
                  <tr key={user.username} className="hover:bg-gray-50 transition-colors duration-150">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.username}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{moment(user.createdDate).format('DD/MM/YYYY HH:mm')}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{Array.isArray(user.groups) ? user.groups.join(', ') : user.groups}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      );
    }

    return <MessageCard message="No new users found for the selected date range" />;
  }

  const titleWithCount = usersData?.length > 0
    ? `📊 ${usersData.length} New Users`
    : 'New Users';

  return (
    <div className="p-4 min-h-screen bg-gray-100">
      <h1 className="text-md font-light text-gray-800 mb-6">
        {titleWithCount}
      </h1>
      <div className="bg-white p-6 rounded-lg shadow-md mb-6">
        <div className="flex flex-wrap items-center gap-4 mb-4">
          <DateRangePicker
            selectedStartDate={selectedStartDate}
            setSelectedStartDate={setSelectedStartDate}
            selectedEndDate={selectedEndDate}
            setSelectedEndDate={setSelectedEndDate}
            selectedStartTime={selectedStartTime}
            setSelectedStartTime={setSelectedStartTime}
            selectedEndTime={selectedEndTime}
            setSelectedEndTime={setSelectedEndTime}
            onDateChange={handleDateChangeCallback}
            onApply={() => {
              if (selectedFormattedStartDate && selectedFormattedEndDate) {
                setIsSearchTriggered(true);
                refetch();
              }
            }}
            datesOptional={false}
          />

        </div>
      </div>
      <div>
        {renderContent()}
      </div>
    </div>
  );
}
